import React from "react";
import {
  useAuthentication,
  useRetrieveAccessRestrictions,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneRole,
} from "@planzone/view-models";
import { RestrictedAreas } from "@planzone/dto";

function useRole() {
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    role,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrievePlanzoneRole,
  } = useRetrievePlanzoneRole();
  const { account, hasSession } = useAuthentication();

  React.useEffect(() => {
    hasSession &&
      !isRequestFailure &&
      !isRequestSuccess &&
      !isRequestPending &&
      retrievePlanzoneRole({ planzone, account });
  }, [planzone.uuid, hasSession]);

  return role;
}

function useAccessRestrictions() {
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    accessRestrictions,
    isRequestFailure,
    isRequestSuccess,
    isRequestPending,
    retrieveAccessRestrictions,
  } = useRetrieveAccessRestrictions();

  React.useEffect(() => {
    !isRequestFailure &&
      !isRequestSuccess &&
      !isRequestPending &&
      retrieveAccessRestrictions(planzone);
  }, [planzone.uuid]);

  return accessRestrictions;
}

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function useRestrictedArea<RA extends RestrictedAreas[]>(
  areas: RA,
): {
  [Key in `canAccess${Capitalize<RA[number]>}`]: boolean;
} {
  const role = useRole();
  const restrictions = useAccessRestrictions();

  return Object.entries(restrictions).reduce<{
    [Key in `canAccess${Capitalize<RA[number]>}`]: boolean;
  }>(
    (accesses, [area, roleIds]) => ({
      ...accesses,
      [`canAccess${capitalize(area)}`]: !!roleIds.find(
        (roleId) => roleId === role.uuid || roleId === role.name,
      ),
    }),
    {} as {
      [Key in `canAccess${Capitalize<RA[number]>}`]: boolean;
    },
  );
}

export { useRestrictedArea };
